import { TransactionType } from "@wingspanhq/bookkeeping/dist/lib/interfaces";
import {
  toWSDate,
  toWSMoneyString,
  useModalContext,
  useWSSnackbar,
  WSAvatar,
  WSButton,
  WSDateRangeSelectOptions,
  WSElement,
  WSFiltersOld,
  WSFlexBox,
  WSFormOld,
  WSGrid,
  WSIcon,
  WSInfiniteScroll,
  WSInputDateOld,
  WSMessageBox,
  WSPage,
  WSPanel,
  WSPill,
  WSRadioInputGroup,
  WSTable,
  WSTableColumn,
  WSText,
  WSTextInput
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "accounting";
import classNames from "classnames";
import { addDays } from "date-fns";
import flatten from "lodash/flatten";
import isEqual from "lodash/isEqual";
import omit from "lodash/omit";
import reduce from "lodash/reduce";
import React from "react";
import { Route, Switch, useHistory } from "react-router-dom";
import styles from "../../Bookkeeping/screens/dashboard/BookkeepingTransactions.module.scss";
import {
  buildPropsIconByTransaction,
  getTransactionName
} from "../../Bookkeeping/utils";
import { InProgressBanner } from "../../modules/Migration/components/InProgressBanner";
import { selectorIsMigrationInProgress } from "../../modules/Migration/selectorIsMigrationInProgress";
import { WSQueries } from "../../query/WSQuery";
import { useRedeemCashbackBalance } from "../../query/bookkeeping/mutations";
import {
  TransactionParams,
  useBankingAccount,
  useBankingBalance,
  useCashbackEligibility,
  usePostedCashbackBalance,
  useTransactions
} from "../../query/bookkeeping/queries";
import { useUserId } from "../../query/hooks/helpers";
import { useFeatureFlags } from "../../query/hooks/useFeatureFlags";
import {
  useCardsQuery,
  usePaymentsStatusQuery,
  usePayoutSettings
} from "../../query/payments/queries";
import { useUpdateActivity } from "../../query/users/mutations";
import { useActivities, useUserProfile } from "../../query/users/queries";
import { IS_PRODUCTION_ENV } from "../../shared/constants/environment";
import { selectorDefaultDestination } from "../../shared/selectors/selectorDefaultDestination";
import { openIntercomWithMessage } from "../../shared/utils/intercom";
import { BankCardOTPModal } from "../../modules/Wallet/components/BankCard/BankCardOTPModal";
import { BankCardsList } from "../../modules/Wallet/components/BankCardsList/BankCardsList";
import {
  AccountDetailsModal,
  WALLET_ACCOUNT_DETAILS
} from "../../modules/Wallet/modals/AccountDetails";
import {
  UPDATE_DEPOSIT_RATE_MODAL,
  UpdateDepositRateModal
} from "../../modules/Wallet/modals/UpdateDepositRate";
import { PendingTransactionsList } from "./PendingTransactionsList";
import { WalletTransaction } from "../../modules/Wallet/routes/WalletTransaction";
import { AmountColumn } from "../../modules/Wallet/components/AmountColumn";
import {
  getAvailableBalance,
  reversePercent,
  useWalletFilters
} from "../../modules/Wallet/utils";

const columns: Array<WSTableColumn> = [
  {
    config: {
      header: { text: "Merchant" },
      gridTemplateSizeMax: "0.8fr"
    },
    renderFunction: rowData => (
      <WSFlexBox alignItems="center">
        <WSAvatar
          mr="S"
          type="icon"
          {...buildPropsIconByTransaction(rowData.data.source)}
        />
        <WSElement>
          <WSFlexBox.CenterY wrap="nowrap">
            <WSText
              color={rowData.data.business ? "gray700" : "gray500"}
              singleLine
              data-testId="transactionMerchant"
            >
              {rowData.data.merchant}
            </WSText>
            {rowData.data.business ? null : <WSPill ml="M" text="Ignored" />}
          </WSFlexBox.CenterY>
          <WSFlexBox.CenterY>
            <WSText.ParagraphSm
              color={
                rowData.data.business &&
                rowData.data.type !== TransactionType.Transfer
                  ? "gray600"
                  : "gray400"
              }
              singleLine
              formatDate="monthDayYear"
            >
              {rowData.data.date}
            </WSText.ParagraphSm>
            {rowData.data.business && rowData.data.receipt ? (
              <WSIcon block ml="M" name="report" size="S" color="gray400" />
            ) : null}
          </WSFlexBox.CenterY>
        </WSElement>
      </WSFlexBox>
    )
  },
  {
    config: {
      hideOnScreens: ["XS", "S", "M"],
      header: { text: "Category" },
      justify: "end"
    },
    renderFunction: rowData => (
      <WSText
        singleLine
        color={
          rowData.data.business &&
          rowData.data.type !== TransactionType.Transfer
            ? "gray700"
            : "gray500"
        }
      >
        {rowData.data.category
          ? `${rowData.data.category}${
              rowData.data.subcategory ? ` - ${rowData.data.subcategory}` : ""
            }`
          : rowData.data.type}
      </WSText>
    )
  },
  {
    config: {
      hideOnScreens: ["XS"],
      header: { text: "Account" },
      justify: "end"
    },
    renderFunction: rowData => (
      <WSText
        singleLine
        color={
          rowData.data.business &&
          rowData.data.type !== TransactionType.Transfer
            ? "gray700"
            : "gray500"
        }
      >
        {rowData.data.account ? `**** ${rowData.data.account}` : "-"}
      </WSText>
    )
  },
  {
    config: {
      justify: "end",
      header: { text: "Amount" }
    },
    renderFunction: rowData => (
      <AmountColumn transaction={rowData.data.source} />
    )
  }
];

export const WalletDashboard: React.FC = () => {
  const {
    defaultFilters,
    filters,
    setFilters,
    redirectWithFilters
  } = useWalletFilters();

  const { openModal } = useModalContext();
  const { openSnackbar } = useWSSnackbar();
  const history = useHistory();

  const { search, ...params } = filters;
  const userId = useUserId();
  const qCardsQuery = useCardsQuery();
  const qBankingBalance = useBankingBalance();
  const qBankingAccount = useBankingAccount();
  const qPayoutSettings = usePayoutSettings(userId);
  const qTransactions = useTransactions(filters);
  const queryUser = useUserProfile(userId);
  const queryActivity = useActivities(userId);
  const queryPaymentSettings = usePaymentsStatusQuery();
  const queryFeatureFlags = useFeatureFlags();

  const qCashbackEligibility = useCashbackEligibility(userId);
  const qPostedCashbackBalance = usePostedCashbackBalance(userId, {
    enabled: qCashbackEligibility.data?.isUserEligibleForCashback
  });

  const isMigraitonInProgress = selectorIsMigrationInProgress(
    qBankingAccount.data
  );

  const [
    redeemCashbackBalance,
    redeemCashbackBalanceMeta
  ] = useRedeemCashbackBalance();

  const [updateActivity] = useUpdateActivity(userId);

  useActivities(userId, {
    onSuccess(data) {
      if (!data.flows.walletSetup?.complete) {
        updateActivity({
          flows: {
            walletSetup: {
              complete: true,
              version: 2,
              currentStep: 1,
              totalSteps: 2
            }
          }
        });
      }
    }
  });

  const areDefaultFilters = isEqual(defaultFilters, params);

  return (
    <WSQueries
      queries={{
        qCardsQuery,
        qBankingBalance,
        qBankingAccount,
        qPayoutSettings,
        queryUser,
        queryActivity,
        queryPaymentSettings,
        queryFeatureFlags
      }}
    >
      {({
        qCardsQuery,
        qBankingBalance,
        qBankingAccount,
        qPayoutSettings,
        queryUser: { data: user },
        queryActivity: { data: activity },
        queryPaymentSettings: { data: paymentSettings },
        queryFeatureFlags: { data: featureFlags }
      }) => {
        const transactionsList = flatten(qTransactions.data || []);

        const transactionsListMapped = transactionsList.map(transaction => {
          const card = qCardsQuery.data?.find(
            card => card.cardId === transaction.wingspanCardId
          );

          return {
            data: {
              source: transaction,
              merchant: getTransactionName(transaction),
              date: transaction.date,
              type: transaction.type,
              amount: transaction.amount,
              receipt: transaction.receiptFileId,
              business: true,
              category: transaction.wsCategory,
              subcategory: transaction.labels.subcategory,
              account: card?.last4Digits
            },
            id: transaction.transactionId
          };
        });

        const depositRatePercentage =
          selectorDefaultDestination(qPayoutSettings.data)?.percentage || 0;

        const cashbackBalance =
          qPostedCashbackBalance.data?.postedCashbackBalance || 0;

        const availableBalance = getAvailableBalance(qBankingBalance.data);
        const currentBalance = qBankingBalance.data?.balance || 0;
        // TODO: fix interfaces
        const pendingAmounts = (qBankingBalance.data as any)?.pending || 0;

        const isBalanceZero = availableBalance === 0;
        const hasNoGrowthAttibution =
          !activity.context?.growthSource &&
          !activity.context?.growthSourceName;
        const isUserCreatedWithin60Days =
          addDays(new Date(), -60) < user.createdAt;
        const hasPayoutDelay =
          paymentSettings.payoutDelay && paymentSettings.payoutDelay > 4;

        if (
          isBalanceZero &&
          hasNoGrowthAttibution &&
          isUserCreatedWithin60Days &&
          hasPayoutDelay &&
          IS_PRODUCTION_ENV
        ) {
          return (
            <WSPage title="Wingspan Wallet">
              <WSMessageBox.Regular
                onClick={() => {
                  openIntercomWithMessage(
                    "I am reaching out to enable Wingspan Wallet – what do I need to do to get setup?"
                  );
                }}
              >
                Thank you for your interest in the Wingspan Wallet, our team is
                reviewing your request and will be in touch with you shortly.
              </WSMessageBox.Regular>
            </WSPage>
          );
        }

        return (
          <WSPage title="Wingspan Wallet">
            <AccountDetailsModal />
            <UpdateDepositRateModal />

            {isMigraitonInProgress && <InProgressBanner mb="2XL" />}

            <WSGrid data-testid="walletDashboard">
              <WSGrid.Item span={{ m: "6", l: "4" }}>
                <WSPanel m="NONE">
                  <WSFlexBox.CenterY justify="space-between">
                    <WSText weight="medium" py="XS">
                      Available balance
                    </WSText>

                    {qBankingAccount.data.numbers?.account ? (
                      <WSButton.Link
                        my="XS"
                        name="viewRoutingNumber"
                        onClick={() => openModal(WALLET_ACCOUNT_DETAILS)}
                      >
                        {`(...${qBankingAccount.data.numbers.account.slice(
                          qBankingAccount.data.numbers.account.length - 4
                        )})→`}
                      </WSButton.Link>
                    ) : null}
                  </WSFlexBox.CenterY>
                  <WSText.Heading1 mt="XS" mb="XL" singleLine>
                    {formatMoney(availableBalance)}
                  </WSText.Heading1>

                  <WSFlexBox.CenterY justify="space-between" mb="M">
                    <WSText.ParagraphSm color="gray500">
                      Current balance:
                    </WSText.ParagraphSm>
                    <WSText.ParagraphSm color="gray600">
                      {toWSMoneyString(currentBalance)}
                    </WSText.ParagraphSm>
                  </WSFlexBox.CenterY>
                  <WSFlexBox.CenterY justify="space-between">
                    <WSText.ParagraphSm color="gray500">
                      Pending amounts:
                    </WSText.ParagraphSm>
                    <WSText.ParagraphSm color="gray500">
                      {toWSMoneyString(pendingAmounts)}
                    </WSText.ParagraphSm>
                  </WSFlexBox.CenterY>

                  <WSButton.Secondary
                    mt="XL"
                    size="S"
                    fullWidth
                    onClick={() => {
                      history.push("/member/transfer?balance=wallet");
                    }}
                    disabled={isMigraitonInProgress}
                  >
                    Transfer
                  </WSButton.Secondary>
                </WSPanel>
              </WSGrid.Item>

              <WSGrid.Item span={{ m: "6", l: "4" }}>
                <WSPanel m="NONE">
                  <WSText weight="medium" py="XS">
                    Cashback balance
                  </WSText>
                  <WSText.Heading1
                    my="XS"
                    color={cashbackBalance > 1 ? "green500" : "gray200"}
                  >
                    {formatMoney(cashbackBalance)}
                  </WSText.Heading1>
                  <WSText.ParagraphSm color="gray400">
                    &nbsp;
                  </WSText.ParagraphSm>
                  <WSButton
                    kind={cashbackBalance > 1 ? "Secondary" : "Tertiary"}
                    size="S"
                    data-testid="redeemCashback"
                    fullWidth
                    mt="XL"
                    disabled={cashbackBalance < 1}
                    onAsyncClick={async () => {
                      await redeemCashbackBalance(
                        {},
                        {
                          onSuccess() {
                            openSnackbar({
                              type: "success",
                              message: "Cashback successfully sent!"
                            });
                          },
                          onError() {
                            openSnackbar({
                              type: "warning",
                              message: "Failed to send cashback!"
                            });
                          }
                        }
                      );
                    }}
                  >
                    Redeem
                  </WSButton>
                </WSPanel>
              </WSGrid.Item>
              {Number.isInteger(depositRatePercentage) ? (
                <WSGrid.Item span={{ m: "6", l: "4" }}>
                  <WSPanel m="NONE">
                    <WSText weight="medium" py="XS">
                      Deposit rate
                    </WSText>
                    <WSText.Heading1 my="XS" data-testid="depositRate">
                      {reversePercent(depositRatePercentage)}%
                    </WSText.Heading1>
                    <WSText.ParagraphSm color="gray400">
                      of every invoice
                    </WSText.ParagraphSm>
                    <WSButton.Secondary
                      size="S"
                      mt="XL"
                      fullWidth
                      name="updateDepositRate"
                      icon="percent"
                      onClick={() => openModal(UPDATE_DEPOSIT_RATE_MODAL)}
                    >
                      Change Deposit Rate
                    </WSButton.Secondary>
                  </WSPanel>
                </WSGrid.Item>
              ) : null}
            </WSGrid>

            <BankCardsList
              basePath="/member/wallet/dashboard/cards"
              isMigraitonInProgress={isMigraitonInProgress}
            />

            <PendingTransactionsList />

            <WSText.Heading4 my="2XL">Transactions</WSText.Heading4>
            <WSFiltersOld
              className={classNames(styles.filters, styles.sticky)}
              getCount={(defaultValues, currentValues) => {
                const _defaultValues = omit(defaultValues, [
                  "search",
                  "customDateRange"
                ]);
                const _currentValues = omit(currentValues, [
                  "search",
                  "customDateRange"
                ]);

                return reduce<any, any>(
                  _currentValues,
                  (result, value, key) =>
                    isEqual(value, _defaultValues[key])
                      ? result
                      : result.concat(key),
                  []
                ).length;
              }}
              filters={params}
              searchQuery={search}
              defaultFilters={defaultFilters}
              onFilter={(newParams: TransactionParams) => {
                let custom = {};

                if (
                  newParams.dateRange !== filters.dateRange &&
                  newParams.dateRange !== "CUSTOM"
                ) {
                  custom = {
                    customDateRange:
                      newParams.dateRange === "PAST_WEEK"
                        ? WSDateRangeSelectOptions.PastWeek.range
                        : newParams.dateRange === "PAST_MONTH"
                        ? WSDateRangeSelectOptions.PastMonth.range
                        : newParams.dateRange === "PAST_YEAR"
                        ? WSDateRangeSelectOptions.Past12Months.range
                        : []
                  };
                }

                if (newParams.category !== "CUSTOM") {
                  newParams.wsCategory = undefined;
                  newParams.type = undefined;
                  newParams.subcategory = undefined;
                  newParams.nonBusiness = undefined;
                }

                setFilters({
                  ...filters,
                  ...(newParams as TransactionParams),
                  ...custom
                });
              }}
              onSearch={newSearch =>
                setFilters({
                  ...filters,
                  search: newSearch
                })
              }
            >
              <WSFormOld.Field
                name="dateRange"
                label="Date range"
                mb="XL"
                component={WSRadioInputGroup}
                componentProps={{
                  options: [
                    {
                      label: "All time",
                      value: "ALL"
                    },
                    {
                      label: "Past week",
                      value: "PAST_WEEK"
                    },
                    {
                      label: "Past month",
                      value: "PAST_MONTH"
                    },
                    {
                      label: "Past year",
                      value: "PAST_YEAR"
                    },
                    {
                      label: (
                        <WSFormOld.Context>
                          {({ setValue }) => (
                            <WSFlexBox
                              alignItems="center"
                              className={styles.dateRange}
                            >
                              <WSText mr="M">From</WSText>
                              <WSFormOld.Field
                                name="customDateRange"
                                component={WSInputDateOld}
                                componentProps={{
                                  selectionMode: "range",
                                  onFocus() {
                                    setValue("dateRange", "CUSTOM");
                                  }
                                }}
                              />
                            </WSFlexBox>
                          )}
                        </WSFormOld.Context>
                      ),
                      value: "CUSTOM"
                    }
                  ]
                }}
              />

              <WSFormOld.Field
                hidden={true}
                label="Type"
                name="type"
                component={WSTextInput}
              />
              <WSFormOld.Field
                hidden={true}
                name="wsCategory"
                component={WSTextInput}
              />
              <WSFormOld.Field
                hidden={true}
                name="subcategory"
                component={WSTextInput}
              />
              <WSFormOld.Field
                hidden={true}
                name="nonBusiness"
                component={WSTextInput}
              />
              <WSFormOld.Field
                hidden={true}
                name="isPositive"
                component={WSTextInput}
              />
            </WSFiltersOld>
            <WSElement py="XL" />
            <WSInfiniteScroll
              onLoad={() => {
                qTransactions.fetchMore();
              }}
              loadMore={transactionsList.length > 0}
              endOfList={!qTransactions.canFetchMore}
              loading={!!qTransactions.isFetchingMore}
            >
              <WSTable
                showHeader={false}
                mb="M"
                loading={qTransactions.isLoading}
                columns={columns}
                onRowClick={item => {
                  redirectWithFilters(
                    `/member/wallet/dashboard/transactions/${item.data.source.transactionId}`
                  );
                }}
                getGroupName={item => {
                  const year = new Date(item.data.date).getFullYear();
                  const currentYear = new Date().getFullYear();

                  const month = toWSDate(item.data.date, "month").toString();

                  if (year === currentYear) {
                    return month;
                  }

                  return `${month} ${year}`;
                }}
                tableData={transactionsListMapped}
                bottomMessage={
                  qTransactions.isLoading === false &&
                  transactionsList.length === 0 &&
                  (!areDefaultFilters || search ? (
                    <>
                      No transactions found.{" "}
                      <WSText.ParagraphXs
                        color="blue500"
                        inline
                        onClick={() => setFilters(defaultFilters)}
                      >
                        Show full transaction history.
                      </WSText.ParagraphXs>
                    </>
                  ) : (
                    "No transactions for this date range."
                  ))
                }
              />
            </WSInfiniteScroll>

            <Switch>
              <Route
                path="/member/wallet/dashboard/transactions/:transactionId"
                exact
                component={WalletTransaction}
              />
            </Switch>

            <BankCardOTPModal />
          </WSPage>
        );
      }}
    </WSQueries>
  );
};
